import { useIntl } from 'react-intl';
import { CreationPageReason, CreationPageType, PageTypeInference } from './inferPageType';

/**
 * The one line under the conclusion.
 *
 * It reports the reason the sheet actually used, not the type it landed on —
 * so an artist who overrides the switch still sees why we disagreed, which is
 * the fact worth knowing before they create the page.
 */
export function useInferenceReason(inference: PageTypeInference, chosen: CreationPageType): string {
  const intl = useIntl();

  if (chosen !== inference.type) {
    return chosen === CreationPageType.presave
      ? intl.formatMessage({
          id: 'app.sml.create.why.overridden_presave',
          defaultMessage: 'You asked for a pre-save. Fans save it the moment you mark it released.',
        })
      : intl.formatMessage({
          id: 'app.sml.create.why.overridden_release',
          defaultMessage: 'You asked for a release page. Every tap routes straight to a service.',
        });
  }

  switch (inference.reason) {
    case CreationPageReason.unreleasedRow:
      return intl.formatMessage({
        id: 'app.sml.create.why.unreleased_row',
        defaultMessage: 'Your catalog says this one is not out yet.',
      });
    case CreationPageReason.futureDate:
      return intl.formatMessage({
        id: 'app.sml.create.why.future_date',
        defaultMessage: 'The release date is still ahead, so the page collects until it lands.',
      });
    case CreationPageReason.pastDate:
      return intl.formatMessage({
        id: 'app.sml.create.why.past_date',
        defaultMessage: 'Already out — every tap routes straight to a service.',
      });
    case CreationPageReason.alreadyLinked:
      return intl.formatMessage({
        id: 'app.sml.create.why.already_linked',
        defaultMessage: 'We found it on music services, so it is out somewhere.',
      });
    case CreationPageReason.nothingToRouteTo:
    default:
      return intl.formatMessage({
        id: 'app.sml.create.why.nothing_to_route_to',
        defaultMessage: 'Nothing to route to yet, so the page collects instead.',
      });
  }
}
